/**
 * faq-accordion.js — smooth single-open accordion over native <details>.
 * We intercept the summary click and animate the panel height (Web Animations
 * API) so open/close slide instead of snapping; opening one closes the rest.
 * Falls back to instant native toggling without JS or with reduced-motion.
 */
import { Component } from '@theme/component';

const DUR_OPEN = 300;
const DUR_CLOSE = 240;
const EASE = 'cubic-bezier(0.33, 1, 0.68, 1)';

class FaqAccordion extends Component {
  mounted() {
    this.items = Array.from(this.querySelectorAll('details'));
    this.classList.add('is-enhanced');
    this.items.forEach((detail) => {
      detail.classList.toggle('is-open', detail.open);
      const summary = detail.querySelector('summary');
      if (summary) summary.addEventListener('click', (event) => this._onClick(event, detail));
    });
  }

  _reduced() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  _onClick(event, detail) {
    event.preventDefault();
    if (detail._animating) return;
    if (detail.open) {
      this._collapse(detail);
    } else {
      this.items.forEach((other) => {
        if (other !== detail && other.open) this._collapse(other);
      });
      this._expand(detail);
    }
  }

  _expand(detail) {
    detail.open = true;
    detail.classList.add('is-open');
    const panel = detail.querySelector('.faq__a');
    if (this._reduced() || !panel) return;
    const target = panel.firstElementChild.getBoundingClientRect().height;
    detail._animating = true;
    const anim = panel.animate(
      [{ height: '0px', opacity: 0 }, { height: `${target}px`, opacity: 1 }],
      { duration: DUR_OPEN, easing: EASE }
    );
    anim.onfinish = () => { detail._animating = false; };
  }

  _collapse(detail) {
    detail.classList.remove('is-open');
    const panel = detail.querySelector('.faq__a');
    if (this._reduced() || !panel) { detail.open = false; return; }
    const start = panel.firstElementChild.getBoundingClientRect().height;
    detail._animating = true;
    const anim = panel.animate(
      [{ height: `${start}px`, opacity: 1 }, { height: '0px', opacity: 0 }],
      { duration: DUR_CLOSE, easing: EASE }
    );
    anim.onfinish = () => { detail.open = false; detail._animating = false; };
  }
}

customElements.define('faq-accordion-component', FaqAccordion);
